import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Ca1 from '../assets/card-founder_maurice.png';
import Ca2 from '../assets/card-founder_patrick.png';
import Ca3 from '../assets/card-founder_ulysse.png';

gsap.registerPlugin(ScrollTrigger);

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 100vh;
  margin-top: 3rem;
  margin-bottom: 5rem;

  @media only screen and (min-device-width: 1024px) and (max-device-width: 1100px) {
    min-height: auto;
    margin-top: 0rem;
    margin-bottom: 20rem;
  }
  @media only screen and (min-device-width: 760px) and (max-device-width: 1023px) {
   min-height: auto;
   margin-top: 0rem;
   margin-bottom: 6rem;
  }
  @media (max-width: 760px) {
    margin-top: -2rem;
    margin-bottom: 2rem; 
    min-height: auto;
    overflow:hidden;
  }
`;

const Title = styled.p`
  font-size: 8rem;
  color: #fff;
  font-family: 'MongoGrotesqueLight';
  margin: -1.5% 0 4rem;

  @media (max-width: 900px) {
    font-size: 5rem;
  }
  @media (max-width: 410px) {
    font-size: 4rem;
  }
`;

const IconRow2 = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 220px;
  margin-bottom: 2rem;
  position: relative;
  

  @media (max-width: 760px) {

  }
`;

const IconCard = styled.img`
  width: 300px;
  height: 420px;
  object-fit: contain;
  position: absolute;
  top: 0;
  transform-origin: bottom center;

  &:nth-child(1) {
    transform: translateX(-20px) rotate(-15deg);
    z-index: 1;
    left: 28%;
    top: 2%;
  }

  &:nth-child(2) {
    transform: translateX(-50%) rotate(0deg);
    z-index: 3;
    left: 50%;
    top: -10%;
  }

  &:nth-child(3) {
    transform: translateX(20px) rotate(15deg);
    z-index: 1;
    left: 50%;
    top: 2%;
  }

  @media (min-width: 1500px) {
    &:nth-child(1) { left: 31%; transform: translateX(-20px) rotate(-10deg); transform-origin: right center; z-index: 1; }
    &:nth-child(2) { left: 50%; transform: translateX(-50%) translateY(-5%) rotate(0deg); transform-origin: center bottom; z-index: 3; }
    &:nth-child(3) { left: 51%; transform: translateX(20px) rotate(12deg); transform-origin: left center; z-index: 1; }
  }

  @media only screen and (min-device-width: 900px) and (max-device-width: 1100px) {
    &:nth-child(1) { left: 15%; transform: translateX(-20px) rotate(-10deg); transform-origin: right center; z-index: 1; }
    &:nth-child(2) { left: 50%; transform: translateX(-50%) translateY(-5%) rotate(0deg); transform-origin: center bottom; z-index: 3; }
    &:nth-child(3) { left: 51%; transform: translateX(20px) rotate(12deg); transform-origin: left center; z-index: 1; }
  }

  @media only screen and (min-device-width: 760px) and (max-device-width: 900px) {
    &:nth-child(1) { left: 5%; transform: translateX(-20px) rotate(-10deg); transform-origin: right center; z-index: 1; }
    &:nth-child(2) { left: 50%; transform: translateX(-50%) translateY(-5%) rotate(0deg); transform-origin: center bottom; z-index: 3; }
    &:nth-child(3) { left: 51%; transform: translateX(20px) rotate(12deg); transform-origin: left center; z-index: 1; }
  }

  @media (max-width: 760px) {
    width: 140px;
    height: 250px;
    &:nth-child(1) { left: 7%; transform: translateX(-20px) rotate(-10deg); }
    &:nth-child(2) { left: 50%; transform: translateX(-50%) translateY(-5%) rotate(0deg); top: -10%; }
    &:nth-child(3) { left: 53%; transform: translateX(20px) rotate(12deg); }
  }

  @media (max-width: 414px) {
    width: 140px;
    height: 250px;
    &:nth-child(1) { left: 8%; transform: translateX(-20px) rotate(-10deg); }
    &:nth-child(2) { left: 49%; transform: translateX(-50%) translateY(-5%) rotate(0deg); top: -10%; }
    &:nth-child(3) { left: 49%; transform: translateX(20px) rotate(12deg); }
  }
`;

const Fondateurs: React.FC = () => {
  const stackRef = useRef<HTMLDivElement>(null);
  const leftRef  = useRef<HTMLImageElement>(null);
  const rightRef = useRef<HTMLImageElement>(null);

  // Detecte le mobile selon tes breakpoints CSS (≤ 760px)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 760);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLayoutEffect(() => {
    if (!stackRef.current || !leftRef.current || !rightRef.current) return;

    const offset = isMobile ? 30 : 100;           // ← moins d’éloignement sur mobile
    const start  = isMobile ? 'top 80%' : 'bottom 70%';

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: stackRef.current,
        start,
        once: true,
      }
    });

    tl.to(leftRef.current,  { x: -offset, duration: 1, ease: 'power4.out' })
      .to(rightRef.current, { x:  offset, duration: 1, ease: 'power4.out' }, '<');

    return () => {
      ScrollTrigger.getAll().forEach(st => st.kill());
    };
  }, [isMobile]);


  return (
    <Content>
      <Title>LES FONDATEURS</Title>
      <IconRow2 ref={stackRef}>
        <IconCard ref={leftRef} src={Ca1} alt="Fondateur 1" />
        <IconCard src={Ca2} alt="Fondateur 2" />
        <IconCard ref={rightRef} src={Ca3} alt="Fondateur 3" />
      </IconRow2>
    </Content>
  );
};

export default Fondateurs;